/**
 * Per-portal theme, owned by Fabric's registry rather than by this bundle.
 *
 * Copied verbatim into every module, like `auth.ts` and `api.ts`. The only
 * per-module difference is the id passed in.
 *
 * ## Why it reads from localStorage first
 *
 * The theme has to be on `<html>` before the first paint, and Fabric is a
 * cold-startable Cloud Run service. Waiting for the network would mean either a
 * blank page or a flash of the wrong colours on every load — so the last known
 * answer is applied synchronously and the fetch only corrects it. In the steady
 * state the correction is a no-op, and the very first visit gets the household
 * default, which is what every portal shipped with anyway.
 *
 * Deliberately unauthenticated: a portal must know its colours before anyone
 * signs in, and a theme name is visible in the rendered page regardless.
 */
const DEFAULT_THEME = "stone";
const KEY = "steyn:theme:";

export function applyTheme(portalId: string): void {
  const cached = localStorage.getItem(KEY + portalId);
  document.documentElement.dataset.theme = cached || DEFAULT_THEME;

  const base = import.meta.env.VITE_FABRIC_API_URL;
  if (!base) return;

  // Fire and forget. A portal whose theme is one load stale is fine; a portal
  // that fails to start because Fabric is cold is not.
  void fetch(`${base}/appearance/${portalId}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((v: { theme?: string } | null) => {
      const theme = v?.theme;
      if (!theme || theme === document.documentElement.dataset.theme) return;
      document.documentElement.dataset.theme = theme;
      localStorage.setItem(KEY + portalId, theme);
    })
    .catch(() => {
      /* Offline, or Fabric is down. The cached theme is already applied. */
    });
}
